import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { findSubcategoryBySlug, subcategorySlug } from '../categorySlug';
import {
  categoryBrowsePath,
  categorySearchPath,
} from '../categoryPaths';
import { shouldSubmitSearch } from '../searchSubmit';
import {
  breadcrumbJsonLd,
  subcategoryIntro,
  subcategoryMetaDescription,
  subcategoryPageTitle,
} from '../seo';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import { SearchField } from './SearchField';
import { SearchResultsPage } from './SearchResultsPage';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import './SearchResultsPage.css';
import './CategoryPage.css';

export function SubcategoryPage() {
  const { categorySlug: rawCategorySlug, subcategorySlug: rawSubSlug } = useParams();
  const categorySlug = (rawCategorySlug ?? '').toLowerCase();
  const subSlug = (rawSubSlug ?? '').toLowerCase();
  const navigate = useNavigate();

  const [browseCategories, setBrowseCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [draftQuery, setDraftQuery] = useState('');
  const [scopePillDismissed, setScopePillDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCategory(null);
    setSubcategory(null);
    setDraftQuery('');

    Promise.all([
      fetch('/api/landing-categories.php').then((r) => jsonOrThrow(r)),
      fetch('/api/browse-websites.php').then((r) => jsonOrThrow(r)),
    ])
      .then(([landingData, siteData]) => {
        if (cancelled) {
          return;
        }
        const categories = Array.isArray(landingData?.categories)
          ? landingData.categories
          : [];
        const match = categories.find((c) => c.slug === categorySlug) ?? null;
        const sub = findSubcategoryBySlug(match, subSlug);
        setCategory(match);
        setSubcategory(sub);
        setBrowseCategories(
          Array.isArray(siteData?.categories) ? siteData.categories : [],
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(describeLoadFailure(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, subSlug]);

  useEffect(() => {
    setScopePillDismissed(false);
  }, [categorySlug, subSlug]);

  const categoryName =
    typeof category?.name === 'string' ? category.name.trim() : '';
  const subcategoryName =
    typeof subcategory?.name === 'string' ? subcategory.name.trim() : '';

  const canonicalPath = subcategory
    ? `/shop/${categorySlug}/${subcategorySlug(subcategory)}`
    : undefined;

  const seoJsonLd = useMemo(() => {
    if (!categoryName || !subcategoryName || !canonicalPath) {
      return undefined;
    }
    return breadcrumbJsonLd([
      { name: 'Home', url: '/' },
      { name: categoryName, url: categoryBrowsePath(categorySlug) },
      { name: subcategoryName, url: canonicalPath },
    ]);
  }, [categoryName, subcategoryName, canonicalPath, categorySlug]);

  const embeddedSearch = useMemo(() => {
    if (!subcategory || !categorySlug || scopePillDismissed) {
      return null;
    }
    return {
      shopCategorySlug: categorySlug,
      effectiveQuery: subcategory.search,
      subcategory,
    };
  }, [categorySlug, subcategory, scopePillDismissed]);

  function applySearch(query) {
    const trimmed = query.trim();

    if (scopePillDismissed) {
      if (!shouldSubmitSearch(trimmed, {})) {
        return;
      }
      navigate(categorySearchPath(trimmed, ''));
      return;
    }

    if (!subcategory) {
      return;
    }
    if (!shouldSubmitSearch(trimmed, { shopSlug: categorySlug, subcategory })) {
      return;
    }
    const combined = trimmed
      ? `${trimmed} AND ${subcategory.search}`
      : subcategory.search;
    navigate(categorySearchPath(combined, categorySlug), {
      state: { searchBoxQuery: trimmed },
    });
  }

  return (
    <div className="results-page category-page subcategory-page">
      {subcategoryName && canonicalPath && (
        <PageHead
          title={subcategoryPageTitle(subcategoryName)}
          description={subcategoryMetaDescription(subcategoryName)}
          canonicalPath={canonicalPath}
          jsonLd={seoJsonLd}
        />
      )}
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <CategoryMenuDropdown
              browseCategories={browseCategories}
              activeShopCategorySlug={categorySlug}
              disabled={loading}
            />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="subcategory-search"
              className="results-page__search"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              searchSubmitScope={
                scopePillDismissed
                  ? {}
                  : { shopSlug: categorySlug, subcategory }
              }
              placeholder={
                scopePillDismissed
                  ? 'Search products…'
                  : 'Search in this category…'
              }
              gaSearchLocation="subcategory_page"
              scopePill={
                subcategoryName && !scopePillDismissed
                  ? {
                      label: subcategoryName,
                      onRemove: () => setScopePillDismissed(true),
                    }
                  : null
              }
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <div className="category-page__main subcategory-page__intro-wrap">
        {loading && (
          <p className="category-page__status" aria-live="polite">
            Loading…
          </p>
        )}
        {!loading && loadError && (
          <p className="category-page__status category-page__status--error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && (category == null || subcategory == null) && (
          <div className="category-page__not-found">
            <p className="category-page__status">Subcategory not found.</p>
            <Link className="category-page__back" to="/">
              Back to home
            </Link>
          </div>
        )}
        {!loading && !loadError && category != null && subcategory != null && (
          <>
            <h1 className="category-page__title">{subcategoryName} in Malta</h1>
            <p className="category-page__intro">{subcategoryIntro(subcategoryName)}</p>
          </>
        )}
      </div>

      {embeddedSearch && (
        <SearchResultsPage embedded={embeddedSearch} />
      )}

      <SiteFooter
        browseCategories={browseCategories}
        activeShopCategorySlug={categorySlug}
      />
    </div>
  );
}
